import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { UserProfile, isBusinessUser } from '../models/User';

interface AuthContextType {
  /** The underlying Firebase Auth user (null if logged out) */
  currentUser: FirebaseUser | null;
  /** Firestore user profile (null if logged out or profile not found) */
  userProfile: UserProfile | null;
  /** True while the initial auth state is being resolved */
  loading: boolean;
  /** Convenience flag: true when the user has a business account */
  isBusiness: boolean;
  /** Sign in with email + password */
  login: (email: string, password: string) => Promise<void>;
  /** Create a new account and write the profile to Firestore */
  signup: (
    email: string,
    password: string,
    profile: Omit<UserProfile, 'id' | 'createdAt' | 'email'>
  ) => Promise<void>;
  /** Sign out and clear local state */
  logout: () => Promise<void>;
  /** Send a password-reset email */
  resetPassword: (email: string) => Promise<void>;
  /** Re-fetch the Firestore profile (e.g. after editing) */
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Hook to access auth state and actions.
 * Must be used inside an `<AuthProvider>`.
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ─── Provider ──────────────────────────────────────────────

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // ── helpers ──────────────────────────────────────────────

  const fetchUserProfile = useCallback(async (uid: string): Promise<UserProfile | null> => {
    try {
      const [userDoc, subDoc] = await Promise.all([
        getDoc(doc(db, 'users', uid)),
        getDoc(doc(db, 'subscriptions', uid)),
      ]);
      if (userDoc.exists()) {
        const profile = { id: userDoc.id, ...userDoc.data() } as UserProfile;
        if (subDoc.exists()) {
          profile.subscription = subDoc.data() as UserProfile['subscription'];
        }
        return profile;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }, []);

  // ── auth state listener ──────────────────────────────────

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const profile = await fetchUserProfile(user.uid);
        setUserProfile(profile);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [fetchUserProfile]);

  // ── actions ──────────────────────────────────────────────

  const login = async (email: string, password: string) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const profile = await fetchUserProfile(result.user.uid);
    setUserProfile(profile);
  };

  const signup = async (
    email: string,
    password: string,
    profile: Omit<UserProfile, 'id' | 'createdAt' | 'email'>
  ) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const userProfileData = {
      ...profile,
      email,
      createdAt: serverTimestamp(),
    };
    await setDoc(doc(db, 'users', result.user.uid), userProfileData);
    setUserProfile({ id: result.user.uid, ...profile, email } as UserProfile);
  };

  const logout = async () => {
    await signOut(auth);
    setUserProfile(null);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const refreshProfile = async () => {
    if (currentUser) {
      const profile = await fetchUserProfile(currentUser.uid);
      setUserProfile(profile);
    }
  };

  // ── derived state ────────────────────────────────────────

  const isBusiness = userProfile ? isBusinessUser(userProfile) : false;

  // ── render ───────────────────────────────────────────────

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        userProfile,
        loading,
        isBusiness,
        login,
        signup,
        logout,
        resetPassword,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
